import React, { useEffect, useState } from 'react';
import { X, Building, Users, Edit3, Check } from 'lucide-react';

const fieldConfig = {
  name: { label: 'Company Name *', placeholder: 'Acme Inc.', type: 'text', required: true },
  website: { label: 'Website', placeholder: 'https://www.acme.com', type: 'text' },
  domain: { label: 'Domain', placeholder: 'acme.com', type: 'text' },
  email: { label: 'Contact Email', placeholder: 'info@acme.com', type: 'email' },
  phone: { label: 'Phone', placeholder: '+1 (555) 123-4567', type: 'text' },
  location: { label: 'City / Location', placeholder: 'San Francisco, CA', type: 'text' },
  country: { label: 'Country', placeholder: 'United States', type: 'text' },
  state: { label: 'State / Province', placeholder: 'CA', type: 'text' },
  zip_code: { label: 'Postal / Zip Code', placeholder: '94105', type: 'text' },
  address: { label: 'Street Address', placeholder: '123 Main St Suite 100', type: 'text' }
};

const fieldLayout = [
  ['name'],
  ['website', 'domain'],
  ['email', 'phone'],
  ['location', 'country'],
  ['state', 'zip_code'],
  ['address']
];

const CompanyDetailsModal = ({
  isOpen,
  loading,
  company,
  editing,
  values,
  saving,
  error,
  onClose,
  onStartEdit,
  onCancelEdit,
  onFieldChange,
  onSave
}) => {
  const [activeField, setActiveField] = useState(null);

  useEffect(() => {
    if (!editing) {
      setActiveField(null);
    }
  }, [editing]);

  if (!isOpen) return null;

  const handleFieldActivate = (fieldKey) => {
    if (saving) return;
    if (!editing) {
      onStartEdit();
    }
    setActiveField(fieldKey);
  };

  const handleFieldCancel = () => {
    if (saving) return;
    onCancelEdit();
    setActiveField(null);
  };

  const handleFieldConfirm = () => {
    if (saving) return;
    onSave();
  };

  const renderField = (fieldKey) => {
    const config = fieldConfig[fieldKey];
    const value = editing ? values?.[fieldKey] ?? '' : company?.[fieldKey] ?? '';
    const isActive = editing && activeField === fieldKey;
    const isDisabled = saving || (editing && activeField && activeField !== fieldKey);

    if (isActive) {
      return (
        <div key={fieldKey}>
          <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
            {config.label}
          </label>
          <div className="mt-1 flex items-center gap-2">
            <input
              type={config.type || 'text'}
              value={value}
              onChange={(event) => onFieldChange(fieldKey, event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  handleFieldConfirm();
                } else if (event.key === 'Escape') {
                  event.preventDefault();
                  handleFieldCancel();
                }
              }}
              className="flex-1 rounded-lg border border-blue-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
              placeholder={config.placeholder}
              autoFocus
              disabled={saving}
            />
            <button
              type="button"
              className="p-2 rounded-full bg-green-50 text-green-700 hover:bg-green-100 disabled:opacity-50"
              onClick={handleFieldConfirm}
              disabled={saving}
              title="Save field"
            >
              <Check size={16} />
            </button>
            <button
              type="button"
              className="p-2 rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-50"
              onClick={handleFieldCancel}
              disabled={saving}
              title="Cancel"
            >
              <X size={16} />
            </button>
          </div>
        </div>
      );
    }

    return (
      <div key={fieldKey}>
        <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
          {config.label}
        </label>
        <button
          type="button"
          onClick={() => handleFieldActivate(fieldKey)}
          disabled={isDisabled}
          className={`mt-1 w-full flex items-center justify-between gap-2 rounded-lg border px-3 py-2 text-left text-sm transition ${
            isDisabled
              ? 'border-gray-200 text-gray-400 cursor-not-allowed'
              : 'border-gray-200 hover:border-gray-300 text-gray-800'
          }`}
        >
          <span className={value ? 'break-words' : 'italic text-gray-400'}>
            {value || 'Not provided'}
          </span>
          <Edit3 size={14} className="text-gray-400" />
        </button>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 px-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl p-6 relative">
        <button
          type="button"
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
          aria-label="Close"
          disabled={saving}
        >
          <X size={18} />
        </button>

        <div className="flex flex-col gap-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-blue-600 flex items-center gap-2">
            <Building size={16} />
            Company Details
          </p>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="text-2xl font-semibold text-gray-900 break-words">
              {company?.name || 'Company'}
            </h2>
            <div className="flex items-center gap-2">
              {company?.contact_count !== undefined && (
                <span className="inline-flex items-center gap-1 text-sm text-gray-600 bg-gray-100 px-3 py-1 rounded-full">
                  <Users size={14} />
                  {company.contact_count} {company.contact_count === 1 ? 'contact' : 'contacts'}
                </span>
              )}
            </div>
          </div>
        </div>

        {error && (
          <div className="mt-4 rounded-lg bg-red-50 text-red-700 text-sm px-3 py-2 border border-red-100">
            {error}
          </div>
        )}

        <div className="mt-6">
          {loading ? (
            <div className="flex items-center justify-center py-16 text-gray-500">
              Loading company details...
            </div>
          ) : (
            <div className="space-y-5">
              {fieldLayout.map((row, index) => (
                <div
                  key={index}
                  className={`grid grid-cols-1 gap-4 ${row.length > 1 ? 'sm:grid-cols-2' : ''}`}
                >
                  {row.map((fieldKey) => renderField(fieldKey))}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CompanyDetailsModal;
